import React, { useState } from 'react';
import axios from 'axios';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { formatDate } from '../../common/constants';
import { Delete, Edit } from '@mui/icons-material';
import Popup from '../Popup';
import Notification from '../Notification';
import ConfirmDialog from '../ConfirmDialog';
import EmployeeModifyOptions from '../forms/EmployeeModifyOptions';
import { EmpNameContext } from './EmployeeMasterTable';
import { setLoaderMessage } from '../../utils/loaderManager';

export default function InternApprenticeMasterTable({ rows = [], loading = false, onRefresh }) {
  const safeRows = Array.isArray(rows) ? rows : [];
  const [openPopup, setOpenPopup] = useState(false);
  const [selected, setSelected] = useState({ id: 0, fName: '', lName: '' });
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' });
  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', subTitle: '', onConfirm: null });

  const cellSx = { py: 1.25, px: 1.5, borderBottom: '1px solid #e8e8e8' };
  const headerSx = { background: '#212529', color: 'white', fontWeight: 600, py: 1.25, px: 1.5 };
  const actionCellSx = { ...cellSx, textAlign: 'center', width: 70 };

  const handleDelete = async (deleteID) => {
    setConfirmDialog((p) => ({ ...p, isOpen: false }));
    setLoaderMessage('Deleting employee...');
    try {
      const res = await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/employee-master/delete-employee/` + deleteID);
      if (res.status === 200) {
        if (onRefresh) await onRefresh();
        setNotify({ isOpen: true, message: 'Successfully Deleted!', type: 'error' });
      }
    } catch (e) {
      setNotify({ isOpen: true, message: 'Delete failed', type: 'error' });
    }
  };

  return (
    <Paper elevation={0} sx={{ width: '100%', border: '1px solid #e8e8e8', borderRadius: '8px', overflow: 'hidden' }}>
      <Box sx={{ width: '100%', overflowX: 'auto' }}>
        <TableContainer sx={{ maxHeight: 520 }}>
          <Table stickyHeader size="small" sx={{ minWidth: 2200 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerSx, width: 70, textAlign: 'center' }}></TableCell>
                <TableCell sx={{ ...headerSx, width: 70, textAlign: 'center' }}></TableCell>
                <TableCell sx={headerSx}>Intern/Apprentice Code</TableCell>
                <TableCell sx={headerSx}>Initial</TableCell>
                <TableCell sx={headerSx}>First Name</TableCell>
                <TableCell sx={headerSx}>Last Name</TableCell>
                <TableCell sx={headerSx}>Full Name</TableCell>
                <TableCell sx={headerSx}>Email</TableCell>
                <TableCell sx={headerSx}>Role</TableCell>
                <TableCell sx={headerSx}>Mobile</TableCell>
                <TableCell sx={headerSx}>PAN</TableCell>
                <TableCell sx={headerSx}>Bank A/c No</TableCell>
                <TableCell sx={headerSx}>IFSC</TableCell>
                <TableCell sx={headerSx}>Bank Name</TableCell>
                <TableCell sx={headerSx}>Gender</TableCell>
                <TableCell sx={headerSx}>Marital Status</TableCell>
                <TableCell sx={headerSx}>Birth Date</TableCell>
                <TableCell sx={headerSx}>Address</TableCell>
                <TableCell sx={headerSx}>Pin Code</TableCell>
                <TableCell sx={headerSx}>Date of Joining</TableCell>
                <TableCell sx={headerSx}>Termination Date</TableCell>
                <TableCell sx={headerSx}>Company</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={13} sx={{ py: 3, textAlign: 'center', color: '#616161' }}>
                    Loading...
                  </TableCell>
                </TableRow>
              ) : safeRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={13} sx={{ py: 3, textAlign: 'center', color: '#616161' }}>
                    No records found.
                  </TableCell>
                </TableRow>
              ) : (
                safeRows.map((r, idx) => (
                  <TableRow
                    key={r.employee_id ?? `${r.employee_code ?? 'row'}-${idx}`}
                    sx={{
                      backgroundColor: idx % 2 === 0 ? '#fafafa' : '#fff',
                      '& td': { borderBottom: '1px solid #e8e8e8' },
                    }}
                  >
                    <TableCell sx={actionCellSx}>
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => {
                          setSelected({ id: r.employee_id, fName: r.first_name ?? '', lName: r.last_name ?? '' });
                          setOpenPopup(true);
                        }}
                      >
                        <Edit />
                      </button>
                    </TableCell>
                    <TableCell sx={actionCellSx}>
                      <button
                        type="button"
                        className="btn btn-outline-danger btn-sm"
                        onClick={() =>
                          setConfirmDialog({
                            isOpen: true,
                            title: 'Are you sure to delete this record?',
                            subTitle: "You can't undo this operation",
                            onConfirm: () => handleDelete(r.employee_id),
                          })
                        }
                      >
                        <Delete />
                      </button>
                    </TableCell>
                    <TableCell sx={cellSx}>{r.employee_code ?? '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.initial ?? '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.first_name ?? '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.last_name ?? '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.full_name ?? '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.email ?? '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.role_name ?? '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.mobile_no ?? '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.pan_card_no ?? '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.ICICI_account_no ?? '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.ifsc_code ?? '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.bank_name ?? '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.gender ?? '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.marital_status ?? '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.birth_date ? formatDate(r.birth_date) : '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.address ?? '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.pin_code ?? '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.date_of_joining ? formatDate(r.date_of_joining) : '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.date_of_termination ? formatDate(r.date_of_termination) : '-'}</TableCell>
                    <TableCell sx={cellSx}>{r.company_name ?? '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Popup title={`Modify Employee - ${selected.fName} ${selected.lName}`} openPopup={openPopup} setOpenPopup={setOpenPopup}>
        <EmpNameContext.Provider
          value={{
            eID: selected.id,
            fName: selected.fName,
            lName: selected.lName,
            isInternApprentice: true,
          }}
        >
          <EmployeeModifyOptions fetch_id={selected.id} onUpdated={onRefresh} />
        </EmpNameContext.Provider>
      </Popup>
      <Notification notify={notify} setNotify={setNotify} />
      <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
    </Paper>
  );
}

